import { Box, Group, Menu, Stack, Text, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

import { IconChevronDown, IconLogout, IconSettings } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { Permission } from '../../../entities/Access';
import { useActiveUserQuery } from '../../../hooks/useActiveUserQuery.hook';
import { useActiveWorkspaceMutation } from '../../../hooks/useActiveWorkspaceMutation.hook';
import { useSignoutMutation } from '../../../hooks/useSignoutMutation.hook';
import { PermissionGuard } from '../../PermissionGuard';
import { Avatar } from '../Avatar/Avatar';

export default function AvatarWithMenu() {
  const signoutMutation = useSignoutMutation();
  const { data: activeUserData } = useActiveUserQuery();
  const activeWorkspaceMutation = useActiveWorkspaceMutation();
  const navigate = useNavigate();
  const { firstName, lastName, activeWorkspace, workspaces } =
    activeUserData.user;

  const isActiveWorkspace = (id: number) => id === activeWorkspace?.id;
  const isSmallScreen = useMediaQuery('(max-width: 991px)');

  return (
    <Menu
      shadow="md"
      width={200}
      withinPortal
      styles={theme => ({
        dropdown: { padding: theme.spacing['02'] + '!important' },
        item: { borderRadius: theme.radius.md },
        itemLabel: {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        itemRightSection: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      })}
    >
      <Menu.Target>
        <UnstyledButton>
          {isSmallScreen ? (
            <SmallMenuAvatar firstName={firstName} lastName={lastName} />
          ) : (
            <LargeMenuAvatar
              firstName={firstName}
              lastName={lastName}
              activeWorkspace={activeWorkspace?.displayName ?? ''}
            />
          )}
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        {workspaces.map(workspace => (
          <Menu.Item
            key={workspace.id}
            onClick={() => {
              if (isActiveWorkspace(workspace.id)) {
                return;
              }

              activeWorkspaceMutation.mutate(workspace.id);
            }}
          >
            <Group noWrap align="center" position="apart" c="primary.7">
              <Text
                variant="sm"
                c={isActiveWorkspace(workspace.id) ? 'primary.7' : 'gray.9'}
                truncate
              >
                {workspace.displayName}
              </Text>

              <PermissionGuard permission={Permission.CREATE_USER}>
                {isActiveWorkspace(workspace.id) && (
                  <Box
                    sx={theme => ({
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.colors.gray[9],
                      cursor: 'pointer',
                      '&:hover': {
                        color: theme.colors.primary[7],
                      },
                    })}
                    onClick={() => {
                      navigate('/workspace-settings');
                    }}
                  >
                    <IconSettings size={16} />
                  </Box>
                )}
              </PermissionGuard>
            </Group>
          </Menu.Item>
        ))}

        <Menu.Item
          color="red"
          icon={<IconLogout size={14} />}
          onClick={() => signoutMutation.mutate()}
        >
          Déconnexion
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

type MenuAvatarProps = {
  firstName: string;
  lastName: string;
  activeWorkspace: string;
};

function SmallMenuAvatar({
  firstName,
  lastName,
}: Omit<MenuAvatarProps, 'activeWorkspace'>) {
  return (
    <Avatar size="lg" radius="md">
      {firstName[0] + lastName[0]}
    </Avatar>
  );
}

function LargeMenuAvatar({
  firstName,
  lastName,
  activeWorkspace,
}: MenuAvatarProps) {
  return (
    <Group noWrap spacing="02" maw={164}>
      <Avatar size="lg" radius="md">
        {firstName[0] + lastName[0]}
      </Avatar>
      <Stack spacing={0} w={124}>
        <Text
          variant="sm"
          fw={500}
          c="gray.9"
          lineClamp={1}
        >{`${firstName} ${lastName}`}</Text>
        <Group noWrap spacing="01" align="center" c="primary.7">
          <Text variant="xs" fw={500} truncate>
            {activeWorkspace}
          </Text>
          <IconChevronDown size={16} />
        </Group>
      </Stack>
    </Group>
  );
}
